/**
 * Die Events des Core (KONZEPT.md §3.5).
 *
 * Module reagieren auf Ereignisse, statt zu fragen. Beispiel aus dem Konzept:
 * Das Ausleih-Modul hoert auf `booking.created`. Wird ein ausgeliehener
 * Artikel irgendwo eingebucht, schliesst das Modul die Position von selbst ab
 * -- ohne dass jemand den Ruecknahmedialog benutzt hat.
 *
 * Events werden erst **nach** dem Festschreiben der Buchung veroeffentlicht.
 * Ein Modul, das auf ein Event reagiert, findet die Buchung also garantiert in
 * der Datenbank vor.
 */
import { z } from "zod";
import { Id, Zeitpunkt } from "./basis.js";
import { Verbleib } from "./enums.js";
export const EventName = z.enum([
    "booking.created",
    "item.status_changed",
    "item.location_changed",
    "item.verbleib_changed",
    "einsatzstelle.created",
    "einsatzstelle.closed",
    "sync.conflict_resolved",
]);
/** Jede Buchung, auch die aus dem Offline-Abgleich. */
export const BookingCreated = z.object({
    name: z.literal("booking.created"),
    zeitpunkt: Zeitpunkt,
    buchung_id: Id,
    artikel_id: Id,
    /** `null`, wenn der Core selbst gebucht hat. */
    modul: z.string().nullable(),
});
/** Der Status hat gewechselt, etwa von einsatzbereit auf defekt. */
export const ItemStatusChanged = z.object({
    name: z.literal("item.status_changed"),
    zeitpunkt: Zeitpunkt,
    artikel_id: Id,
    /** Schluessel des Status; `null`, wenn der Artikel noch keinen Zustand hatte. */
    vorher: z.string().nullable(),
    nachher: z.string(),
    buchung_id: Id,
});
/**
 * Der Verbleib hat gewechselt, etwa vom Lager in den Einsatz. Das Ereignis,
 * auf das Ausleihe und Einsatzstellen hoeren (KONZEPT.md §15.5).
 */
export const ItemVerbleibChanged = z.object({
    name: z.literal("item.verbleib_changed"),
    zeitpunkt: Zeitpunkt,
    artikel_id: Id,
    vorher: Verbleib.nullable(),
    nachher: Verbleib,
    buchung_id: Id,
});
export const ItemLocationChanged = z.object({
    name: z.literal("item.location_changed"),
    zeitpunkt: Zeitpunkt,
    artikel_id: Id,
    vorher: Id.nullable(),
    nachher: Id.nullable(),
    buchung_id: Id,
});
export const EinsatzstelleCreated = z.object({
    name: z.literal("einsatzstelle.created"),
    zeitpunkt: Zeitpunkt,
    einsatzstelle_id: Id,
});
export const EinsatzstelleClosed = z.object({
    name: z.literal("einsatzstelle.closed"),
    zeitpunkt: Zeitpunkt,
    einsatzstelle_id: Id,
});
/**
 * Eine Buchung wurde von einer neueren ueberstimmt (KONZEPT.md §15.3).
 *
 * Der Client zeigt daraus den Hinweis aus §15.4 an. Ein blockierender Dialog
 * entsteht daraus ausdruecklich nicht -- niemand loest im Einsatz Konflikte
 * auf.
 */
export const SyncConflictResolved = z.object({
    name: z.literal("sync.conflict_resolved"),
    zeitpunkt: Zeitpunkt,
    artikel_id: Id,
    /** Die Buchung, die den Zustand nicht geaendert hat. */
    ueberstimmte_buchung_id: Id,
    /** Die Buchung, die gilt. */
    gueltige_buchung_id: Id,
});
export const CoreEvent = z.discriminatedUnion("name", [
    BookingCreated,
    ItemStatusChanged,
    ItemLocationChanged,
    ItemVerbleibChanged,
    EinsatzstelleCreated,
    EinsatzstelleClosed,
    SyncConflictResolved,
]);
//# sourceMappingURL=events.js.map